import {
  MONACO_ACCESS_TOKEN_COOKIE,
  MONACO_REFRESH_TOKEN_COOKIE,
} from "@nitro-ape/shared/lib/constants";
import { MonacoSDK } from "@0xmonaco/core";
import { isServer } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { propagateMonacoAccessToken } from "./get-wallet-client";

export function getMonacoAccessToken(): string | undefined {
  if (isServer) return undefined;
  return Cookies.get(MONACO_ACCESS_TOKEN_COOKIE);
}

export function getMonacoRefreshToken(): string | undefined {
  if (isServer) return undefined;
  return Cookies.get(MONACO_REFRESH_TOKEN_COOKIE);
}

export function setMonacoTokens(
  accessToken: string,
  refreshToken: string,
): void {
  if (isServer) return;
  Cookies.set(MONACO_ACCESS_TOKEN_COOKIE, accessToken);
  Cookies.set(MONACO_REFRESH_TOKEN_COOKIE, refreshToken);
}

export function clearMonacoTokens(): void {
  if (isServer) return;
  Cookies.remove(MONACO_ACCESS_TOKEN_COOKIE);
  Cookies.remove(MONACO_REFRESH_TOKEN_COOKIE);
}

function isAuthError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (
      message.includes("401") ||
      message.includes("403") ||
      message.includes("unauthorized") ||
      message.includes("forbidden")
    ) {
      return true;
    }
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error.status === 401 || error.status === 403)
  ) {
    return true;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "statusCode" in error &&
    (error.statusCode === 401 || error.statusCode === 403)
  ) {
    return true;
  }

  return false;
}

let refreshPromise: Promise<boolean> | null = null;
let lastRefreshTime = 0;
const REFRESH_DEBOUNCE_MS = 5000;

export async function refreshMonacoToken(sdk: MonacoSDK): Promise<boolean> {
  const now = Date.now();

  if (refreshPromise && now - lastRefreshTime < REFRESH_DEBOUNCE_MS) {
    return refreshPromise;
  }

  lastRefreshTime = now;
  refreshPromise = (async () => {
    const refreshToken = getMonacoRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const newTokens = await sdk.auth.refreshToken(refreshToken);
      setMonacoTokens(newTokens.accessToken, refreshToken);
      propagateMonacoAccessToken(sdk, newTokens.accessToken);
      return true;
    } catch (error) {
      console.error("Failed to refresh Monaco token:", error);
      clearMonacoTokens();
      return false;
    }
  })();

  return refreshPromise;
}

/**
 * Wrapper that handles 401/403 errors with automatic token refresh and retry.
 * Use this to wrap SDK operations that require authentication.
 *
 * @example
 * const profile = await withMonacoAuth(sdk, () => sdk.profile.getProfile());
 */
export async function withMonacoAuth<T>(
  sdk: MonacoSDK,
  operation: () => Promise<T>,
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (isAuthError(error)) {
      const refreshSuccess = await refreshMonacoToken(sdk);

      if (refreshSuccess) {
        return await operation();
      }
    }

    throw error;
  }
}
